/**
 * AdminRoles — /admin/roles
 *
 * List every admin, checkbox their scopes, save. Mutations require step-up
 * re-authentication — a modal prompts for the operator's primary password
 * and the request is retried with `X-Confirm-Password: <pw>` header.
 *
 * Roles:
 *   • compliance_auditor — read-only compliance
 *   • support_moderator  — day-to-day ops
 *   • system_operator    — destructive + infra (this page IS operator-only)
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PhaseBanner } from "@/components/PhaseBanner";
import { AppShell } from "@/components/AppShell";
import { T } from "@/lib/theme";

const ALL_ROLES = ["compliance_auditor", "support_moderator", "system_operator"];

export default function AdminRoles() {
  const [staff, setStaff] = useState([]);
  const [available, setAvailable] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState(null); // { userId, targetRoles, currentRoles }
  const [pw, setPw] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, a] = await Promise.all([
        api.get("/admin/roles/staff"),
        api.get("/admin/roles/available"),
      ]);
      setStaff(s.data.users || []);
      setAvailable(a.data.roles || []);
    } catch (e) {
      toast.error("Failed to load admin roles");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openStepUp = (userId, currentRoles, targetRoles) => {
    setPending({ userId, currentRoles, targetRoles });
    setPw("");
  };

  const submitStepUp = async () => {
    if (!pending || !pw) return;
    setSaving(true);
    const { userId, currentRoles, targetRoles } = pending;
    const current = new Set(currentRoles);
    const target = new Set(targetRoles);
    const toAssign = [...target].filter((r) => !current.has(r));
    const toRevoke = [...current].filter((r) => !target.has(r));

    const headers = { "X-Confirm-Password": pw };
    try {
      if (toAssign.length) {
        await api.post("/admin/roles/assign",
          { user_id: userId, roles: toAssign, reason: "console update" },
          { headers });
      }
      if (toRevoke.length) {
        await api.post("/admin/roles/revoke",
          { user_id: userId, roles: toRevoke, reason: "console update" },
          { headers });
      }
      toast.success("Roles updated");
      setPending(null); setPw("");
      await load();
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      if (String(detail).toLowerCase().includes("step-up") || e.response?.status === 401) {
        toast.error("Password incorrect");
      } else {
        toast.error(String(detail));
      }
    } finally { setSaving(false); }
  };

  return (
    <AppShell>
      <div data-testid="admin-roles-page" style={{ padding: "24px 32px", maxWidth: 1100, margin: "0 auto" }}>
        <PhaseBanner />
        <h1 style={{ fontSize: 26, fontWeight: 800, color: T.navy, margin: "0 0 6px",
          letterSpacing: "-0.02em" }}>Admin roles</h1>
        <p style={{ color: T.muted, fontSize: 14, margin: "0 0 24px", lineHeight: 1.6 }}>
          Grant least-privilege scopes. Mutations require you to re-enter your primary password.
          Role changes are logged in the hash-chained audit trail.
        </p>

        {loading && <div style={{ color: T.muted }}>Loading…</div>}

        {!loading && (
          <div data-testid="admin-roles-table" style={{
            background: "#fff", border: `1px solid ${T.border}`, borderRadius: 12,
            overflow: "hidden",
          }}>
            <div style={{
              display: "grid", gridTemplateColumns: "2fr repeat(3, minmax(160px, 1fr)) 120px",
              gap: 12, padding: "14px 20px", background: "#F8FAFC",
              borderBottom: `1px solid ${T.border}`, fontWeight: 700, fontSize: 12,
              color: T.muted, letterSpacing: "0.06em", textTransform: "uppercase",
            }}>
              <div>Admin</div>
              {ALL_ROLES.map((r) => {
                const meta = available.find((a) => a.value === r);
                return <div key={r} title={meta?.description || r}>{meta?.label || r}</div>;
              })}
              <div style={{ textAlign: "right" }}>Save</div>
            </div>

            {staff.length === 0 && (
              <div style={{ padding: 30, color: T.muted, textAlign: "center" }}>
                No admins with an active profile.
              </div>
            )}

            {staff.map((u) => (
              <StaffRow key={u.id} user={u} onSave={openStepUp} />
            ))}
          </div>
        )}
      </div>

      {/* Step-up password modal */}
      {pending && (
        <div data-testid="stepup-modal" style={{
          position: "fixed", inset: 0, zIndex: 400, background: "rgba(10,22,40,0.72)",
          display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
        }}>
          <div style={{ background: "#fff", borderRadius: 14, padding: 28, maxWidth: 420, width: "100%" }}>
            <h2 style={{ margin: "0 0 8px", fontSize: 18, fontWeight: 800, color: T.navy }}>
              Confirm your password
            </h2>
            <p style={{ margin: "0 0 18px", fontSize: 13, color: T.muted, lineHeight: 1.6 }}>
              Role changes require step-up authentication. Re-enter your primary
              administrative password to save.
            </p>
            <input data-testid="stepup-password" type="password" autoFocus
              value={pw} onChange={(e) => setPw(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && pw && submitStepUp()}
              placeholder="Primary password"
              style={{ width: "100%", padding: "11px 14px", borderRadius: 8,
                border: `1px solid ${T.border}`, fontSize: 14, fontFamily: "inherit",
                marginBottom: 18 }} />
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button data-testid="stepup-cancel"
                onClick={() => { setPending(null); setPw(""); }}
                style={{ background: "transparent", border: `1px solid ${T.border}`,
                  padding: "10px 18px", borderRadius: 8, cursor: "pointer",
                  color: T.muted, fontSize: 13, fontWeight: 600, fontFamily: "inherit" }}>
                Cancel
              </button>
              <button data-testid="stepup-submit" disabled={!pw || saving}
                onClick={submitStepUp}
                style={{ background: T.green, border: "none", padding: "10px 18px",
                  borderRadius: 8, cursor: pw && !saving ? "pointer" : "not-allowed",
                  color: "#fff", fontSize: 13, fontWeight: 700, fontFamily: "inherit",
                  opacity: pw && !saving ? 1 : 0.6 }}>
                {saving ? "Saving…" : "Confirm & save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}

function StaffRow({ user, onSave }) {
  const current = new Set(user.admin_profile?.roles || []);
  const [selected, setSelected] = useState(current);
  const dirty = (() => {
    if (selected.size !== current.size) return true;
    for (const r of selected) if (!current.has(r)) return true;
    return false;
  })();
  const toggle = (r) => {
    const next = new Set(selected);
    if (next.has(r)) next.delete(r); else next.add(r);
    setSelected(next);
  };
  return (
    <div data-testid={`admin-row-${user.id}`}
      style={{ display: "grid", gridTemplateColumns: "2fr repeat(3, minmax(160px, 1fr)) 120px",
        gap: 12, padding: "16px 20px", borderBottom: `1px solid ${T.border}`,
        alignItems: "center", fontSize: 14 }}>
      <div>
        <div style={{ fontWeight: 700, color: T.navy }}>{user.name || user.email}</div>
        <div style={{ fontSize: 11, color: T.muted }}>{user.email}</div>
        {user.admin_profile?.is_active === false && (
          <span data-testid={`admin-inactive-${user.id}`}
            style={{ display: "inline-block", marginTop: 4, fontSize: 10, fontWeight: 700,
              color: "#B45309", background: "#FEF3C7", padding: "2px 6px", borderRadius: 4 }}>
            INACTIVE
          </span>
        )}
      </div>
      {ALL_ROLES.map((r) => (
        <label key={r} style={{ display: "flex", alignItems: "center", gap: 8,
          color: T.navy, cursor: "pointer", userSelect: "none" }}>
          <input data-testid={`admin-role-${r}-${user.id}`}
            type="checkbox" checked={selected.has(r)}
            onChange={() => toggle(r)} />
          <span style={{ fontSize: 11, color: T.muted }}>
            {selected.has(r) ? "granted" : ""}
          </span>
        </label>
      ))}
      <div style={{ textAlign: "right" }}>
        <button data-testid={`admin-save-${user.id}`}
          disabled={!dirty}
          onClick={() => onSave(user.id, [...current], [...selected])}
          style={{ background: dirty ? T.green : "#E5E7EB",
            color: dirty ? "#fff" : T.muted,
            border: "none", padding: "8px 16px", borderRadius: 8,
            cursor: dirty ? "pointer" : "not-allowed",
            fontSize: 12, fontWeight: 700, fontFamily: "inherit" }}>
          Save
        </button>
      </div>
    </div>
  );
}
